import type { ReactElement } from 'react';
import {
  Badge,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { RuleMode, RuleSeverity, WhenLogic } from '@billing/validation/rules';
import { RuleAlertPreview } from './RuleAlertPreview';
import { getRuleAction } from './RuleActionPicker';
import { formatWhenClause, formatValidationSummary } from './ruleUtils';
import type { RuleBuilder } from './useRuleBuilder';
import type { RuleTranslate } from './constants';

interface RuleSummaryProps {
  builder: RuleBuilder;
}

const RulePrioritySummary = ({
  priority,
  translate,
}: {
  priority: string;
  translate: RuleTranslate;
}): ReactElement => (
  <Text size="xs" c="dimmed">
    {translate('pages:rules.builder.edit.priority')}: {priority}
  </Text>
);

const RuleApplyDataSummary = ({ builder }: RuleSummaryProps): ReactElement => (
  <Stack gap={4}>
    <Text size="sm" fw={600}>
      {builder.translate(
        `pages:rules.applyData.${builder.applyBehavior ?? 'automatic'}Title`
      )}
    </Text>
    {builder.applyUpdates.map((update) => (
      <Stack key={update.id} gap={2}>
        <Text size="sm">
          {builder.translate('pages:rules.builder.set')}{' '}
          {builder.translate(`pages:rules.editApplyFields.${update.field}`)}{' '}
          {builder.translate('pages:rules.builder.to')} {update.value}
        </Text>
        <Text size="xs" c="dimmed">
          {builder.translate(
            `pages:rules.applyData.${
              update.field === 'tariffCode'
                ? 'addIfMissing'
                : update.writePolicy ?? 'replace'
            }`
          )}
        </Text>
      </Stack>
    ))}
    {builder.applyBehavior !== 'suggest' && (
      <Text size="xs" c="dimmed">
        {builder.translate(
          `pages:rules.applyData.${builder.applyNotification ?? 'silent'}`
        )}
      </Text>
    )}
    <RulePrioritySummary
      priority={builder.applyPriority}
      translate={builder.translate}
    />
  </Stack>
);

const RuleTariffTransformsSummary = ({
  builder,
}: RuleSummaryProps): ReactElement => (
  <Stack gap={4}>
    <Text size="sm">
      {builder.translate('pages:rules.builder.edit.transforms')}
    </Text>
    <Text size="sm">
      {builder.translate('pages:rules.builder.edit.timeCodes')}:{' '}
      {builder.timeCodeAfterMidnight}, {builder.timeCodeEvening},{' '}
      {builder.timeCodeWeekend}
    </Text>
    <RulePrioritySummary
      priority={builder.transformPriority}
      translate={builder.translate}
    />
  </Stack>
);

const RuleFlagSummary = ({ builder }: RuleSummaryProps): ReactElement => (
  <Stack gap={4}>
    <Text size="sm">
      {builder.effectiveFlagLabel} · {builder.flagSeverity}
    </Text>
    <Text size="sm">{builder.effectiveFlagMessage}</Text>
  </Stack>
);

// Summary sections
const RuleUnsupportedSummary = ({
  builder,
}: RuleSummaryProps): ReactElement => (
  <Text size="sm" c="dimmed">
    {builder.translate('pages:rules.builder.edit.unsupportedSummary')}
  </Text>
);

export const RuleActionSummary = ({
  builder,
}: RuleSummaryProps): ReactElement => {
  if (!builder.isFormSupported)
    return <RuleUnsupportedSummary builder={builder} />;
  const action = getRuleAction(builder.selectedAction, builder.translate);
  return (
    <Stack gap="sm">
      <Badge variant="light" color={action.color}>
        <Group gap="xs">
          {action.Icon && <action.Icon size={12} />}
          {action.label}
        </Group>
      </Badge>
      <Text size="sm" c="dimmed">
        {action.description}
      </Text>
    </Stack>
  );
};

export const RuleWhenSummary = ({
  builder,
}: RuleSummaryProps): ReactElement => (
  <Stack gap="sm">
    {builder.criteriaGroups.map((group, groupIndex) => (
      <Stack key={group.id} gap={4}>
        {groupIndex > 0 && (
          <Text size="xs" fw={700}>
            {builder.translate(
              builder.whenLogic === WhenLogic.All
                ? 'pages:rules.groups.and'
                : 'pages:rules.groups.or'
            )}
          </Text>
        )}
        <Text size="xs" c="dimmed" fw={600}>
          {builder.translate('pages:rules.groups.requirement', {
            number: groupIndex + 1,
          })}
          :{' '}
          {builder.translate(
            group.logic === WhenLogic.Any
              ? 'pages:rules.builder.whenAnyConditionMatches'
              : 'pages:rules.builder.whenAllConditionsMatch'
          )}
        </Text>
        {group.rows.map(({ clause, joinerKey }, index) => (
          <Text size="sm" key={clause.id}>
            {index > 0 && `${builder.translate(joinerKey)} `}
            {formatWhenClause(clause, builder)}
          </Text>
        ))}
      </Stack>
    ))}
  </Stack>
);

export const RuleCriteriaSummary = ({
  builder,
}: RuleSummaryProps): ReactElement => {
  if (!builder.isFormSupported)
    return <RuleUnsupportedSummary builder={builder} />;
  return (
    <Stack gap={4}>
      <RuleWhenSummary builder={builder} />
      {builder.ruleMode === RuleMode.Validate &&
        formatValidationSummary(builder).map((summary, index) => (
          <Text size="sm" key={builder.assertions[index]?.id ?? index}>
            {summary}
          </Text>
        ))}
    </Stack>
  );
};

export const RuleOutcomeSummary = ({
  builder,
}: RuleSummaryProps): ReactElement => {
  if (!builder.isFormSupported)
    return <RuleUnsupportedSummary builder={builder} />;
  switch (builder.ruleMode) {
    case RuleMode.ApplyData:
      return <RuleApplyDataSummary builder={builder} />;
    case RuleMode.TariffTransforms:
      return <RuleTariffTransformsSummary builder={builder} />;
    case 'flagOnly':
      return <RuleFlagSummary builder={builder} />;
    default:
      return (
        <RuleAlertPreview
          severity={builder.severity}
          message={
            builder.assertions.length === 0
              ? builder.ruleMessage
              : builder.effectiveRuleMessage
          }
          translate={builder.translate}
        />
      );
  }
};

export const RuleCreatePreview = ({
  builder,
}: {
  builder: RuleBuilder;
}): ReactElement => {
  const action = getRuleAction(builder.selectedAction, builder.translate);
  const block = builder.severity === RuleSeverity.Block;
  return (
    <Paper
      withBorder
      p="lg"
      radius="lg"
      shadow="xs"
      pos={{ base: 'static', md: 'sticky' }}
      top={'lg'}
      w={{ base: '100%', md: 320 }}
      style={{ flexShrink: 0, overflowWrap: 'anywhere' }}
    >
      <Stack gap="md">
        <Title order={2} size="h4">
          {builder.translate('pages:rules.builder.summaryTitle')}
        </Title>
        <Text c="dimmed" size="sm">
          {builder.translate('pages:rules.builder.summarySubtitle')}
        </Text>
        {action && (
          <Badge
            variant="light"
            color={action.color}
            leftSection={<action.Icon size={14} />}
          >
            {action.label}
          </Badge>
        )}
        <Stack gap="xs">
          <Text size="xs" c="dimmed" fw={700} tt="uppercase">
            {builder.translate(
              builder.whenLogic === WhenLogic.Any
                ? 'pages:rules.groups.whenAnyRequirementMatches'
                : 'pages:rules.groups.whenAllRequirementsMatch'
            )}
          </Text>
          <RuleCriteriaSummary builder={builder} />
        </Stack>
        <Divider />
        <Stack gap="xs">
          <Text size="xs" c="dimmed" fw={700} tt="uppercase">
            {builder.translate('pages:rules.builder.then')}
          </Text>
          {builder.ruleMode === RuleMode.Validate ? (
            <>
              <Text size="sm">
                {block
                  ? builder.translate('pages:rules.builder.blockSummary')
                  : builder.translate('pages:rules.builder.warnSummary')}{' '}
                {builder.ruleMessage.trim() ||
                  builder.translate('pages:rules.builder.messageEmpty')}
              </Text>
              <Text size="sm">
                {block
                  ? builder.translate('pages:rules.builder.noOverride')
                  : builder.translate('pages:rules.builder.canSave')}
              </Text>
            </>
          ) : (
            <RuleOutcomeSummary builder={builder} />
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};
